import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { capitalizeWords } from "../../../Utils";

const formatRupiah = (num) => {
  if (num === null || num === undefined || Number.isNaN(Number(num))) return "0";
  return Number(num).toLocaleString("id-ID");
};

const normalizeMutasiPotonganArgs = (...args) => {
  if (Array.isArray(args[0])) {
    return {
      data: args[0],
      periode: args[1] || "",
      namaBulan: args[2] || "",
      tahun: args[3] || new Date().getFullYear(),
      kodeUk: args[4] || ""
    };
  }

  return {
    data: Array.isArray(args[1]) ? args[1] : [],
    periode: args[0] || "",
    namaBulan: args[2] || "",
    tahun: args[3] || (args[0] ? args[0].slice(0, 4) : new Date().getFullYear()),
    kodeUk: args[4] || ""
  };
};

const mutasipotongan = (...args) => {
  const { data, periode, namaBulan, tahun, kodeUk } = normalizeMutasiPotonganArgs(...args);
  const actualData = Array.isArray(data) ? data : [];

  if (actualData.length === 0) {
    alert("Tidak ada data untuk dicetak.");
    return;
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  const bulanLabel = namaBulan || (periode ? new Date(`${periode.slice(0, 4)}-${periode.slice(4, 6)}-01`).toLocaleString("id-ID", { month: "long" }) : "");
  const tahunLabel = tahun || (periode ? periode.slice(0, 4) : new Date().getFullYear());

  const loadImageAsDataURL = (url) =>
    fetch(url)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );

  const drawHeader = (logoDataUrl) => {
    const logoX = 10;
    const logoY = 8;
    const logoSize = 18;
    const textX = logoX + logoSize + 3;

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoSize, logoSize);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("KPRI SRI REJEKI", textX, 15);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Jl. Donomulyo No.164 Kab. Malang", textX, 21);

    const lineY = 30;
    doc.setLineWidth(0.5);
    doc.line(10, lineY, pageWidth - 10, lineY);

    const reportY = lineY + 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Laporan Mutasi Potongan", pageWidth / 2, reportY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const subtitle = kodeUk ? `${capitalizeWords(bulanLabel)} ${tahunLabel} | Unit Kerja: ${kodeUk}` : `${capitalizeWords(bulanLabel)} ${tahunLabel}`;
    doc.text(subtitle, pageWidth / 2, reportY + 7, { align: "center" });

    return reportY + 13;
  };

  const headers = [[
    { content: "No", styles: { halign: "center" } },
    { content: "No.Anggota", styles: { halign: "center" } },
    { content: "Nama", styles: { halign: "center" } },
    { content: "S.Wajib", styles: { halign: "center" } },
    { content: "S.Hari Raya", styles: { halign: "center" } },
    { content: "Pot.SM", styles: { halign: "center" } },
    { content: "Angsuran", styles: { halign: "center" } },
    { content: "Jasa", styles: { halign: "center" } },
    { content: "Germen", styles: { halign: "center" } },
    { content: "Belanja", styles: { halign: "center" } },
    { content: "Total", styles: { halign: "center" } }
  ]];

  let totalWajib = 0;
  let totalHariRaya = 0;
  let totalPotSM = 0;
  let totalAngsuran = 0;
  let totalJasa = 0;
  let totalGermen = 0;
  let totalBelanja = 0;
  let totalKeseluruhan = 0;

  const tableData = actualData.map((item, index) => {
    const swajib = parseFloat(item.swajib) || 0;
    const shariraya = parseFloat(item.shariraya) || 0;
    const potsm = parseFloat(item.potsm) || 0;
    const angs = parseFloat(item.angs_a) || 0;
    const jasa = parseFloat(item.jasa_a) || 0;
    const germen = parseFloat(item.germen_a) || 0;
    const belanja = parseFloat(item.vbelanja) || 0;
    const totalPerBaris = swajib + shariraya + potsm + angs + jasa + germen + belanja;

    totalWajib += swajib;
    totalHariRaya += shariraya;
    totalPotSM += potsm;
    totalAngsuran += angs;
    totalJasa += jasa;
    totalGermen += germen;
    totalBelanja += belanja;
    totalKeseluruhan += totalPerBaris;

    return [
      { content: index + 1, styles: { halign: "center" } },
      { content: item.noanggota ?? "-", styles: { halign: "center" } },
      { content: capitalizeWords(item.nama || "-"), styles: { halign: "left" } },
      { content: formatRupiah(swajib), styles: { halign: "right" } },
      { content: formatRupiah(shariraya), styles: { halign: "right" } },
      { content: formatRupiah(potsm), styles: { halign: "right" } },
      { content: formatRupiah(angs), styles: { halign: "right" } },
      { content: formatRupiah(jasa), styles: { halign: "right" } },
      { content: formatRupiah(germen), styles: { halign: "right" } },
      { content: formatRupiah(belanja), styles: { halign: "right" } },
      { content: formatRupiah(totalPerBaris), styles: { halign: "right" } }
    ];
  });

  tableData.push([
    { content: "", styles: { halign: "center" } },
    { content: "Laporan Mutasi Potongan", colSpan: 2, styles: { fontStyle: "bold", halign: "center", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalWajib), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalHariRaya), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalPotSM), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalAngsuran), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalJasa), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalGermen), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalBelanja), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } },
    { content: formatRupiah(totalKeseluruhan), styles: { fontStyle: "bold", halign: "right", fillColor: [230, 230, 230] } }
  ]);

  const renderTable = (startY) => {
    const tableWidth = pageWidth - 52;
    const centeredMargin = (pageWidth - tableWidth) / 2;

    autoTable(doc, {
      head: headers,
      body: tableData,
      startY,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [32, 178, 170],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        fontSize: 8,
      },
      bodyStyles: {
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fontSize: 8,
      },
      margin: { left: centeredMargin, right: centeredMargin, top: 20, bottom: 10 },
      tableWidth,
      showHead: "everyPage",
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 20, halign: "center" },
        2: { cellWidth: 50, halign: "left" },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: 22, halign: "right" },
        5: { cellWidth: 18, halign: "right" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 18, halign: "right" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 20, halign: "right" },
        10: { cellWidth: 22, halign: "right" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [230, 230, 230];
        }
      },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 8
        );
      },
    });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  };

  const customLogoUrl = "/koperasihitamputih.png";

  loadImageAsDataURL(customLogoUrl)
    .then((logoDataUrl) => {
      const startY = drawHeader(logoDataUrl);
      renderTable(startY);
    })
    .catch(() => {
      const startY = drawHeader();
      renderTable(startY);
    });
};

export default mutasipotongan;
